import { DropdownButton, Menu, MenuSub } from '@affine/component';
import { BlockCard } from '@affine/component/card/block-card';
import { TemplateListMenuContentScrollable } from '@affine/core/modules/template-doc/view/template-list-menu';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import {
  EdgelessIcon,
  GithubIcon,
  ImportIcon,
  PageIcon,
  TemplateIcon,
} from '@blocksuite/icons/rc';
import type { MouseEvent, PropsWithChildren } from 'react';
import { useCallback, useState } from 'react';

import * as styles from './new-page-button.css';

type NewPageButtonProps = {
  createNewDoc: (e?: MouseEvent) => void;
  createNewPage: (e?: MouseEvent) => void;
  createNewEdgeless: (e?: MouseEvent) => void;
  createFromTemplate?: (templateId: string) => void;
  importFile?: () => void;
  importFromGitHub?: () => void;
  size?: 'small' | 'default';
};

export const CreateNewPagePopup = ({
  createNewPage,
  createNewEdgeless,
  createFromTemplate,
  importFile,
  importFromGitHub,
}: NewPageButtonProps) => {
  const t = useI18n();
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '8px',
      }}
    >
      <BlockCard
        title={t['com.affine.new.page-mode']()}
        desc={t['com.affine.write_with_a_blank_page']()}
        right={<PageIcon width={20} height={20} />}
        onClick={createNewPage}
        onAuxClick={createNewPage}
        data-testid="new-page-button-in-all-page"
      />
      <BlockCard
        title={t['com.affine.new_edgeless']()}
        desc={t['com.affine.draw_with_a_blank_whiteboard']()}
        right={<EdgelessIcon width={20} height={20} />}
        onClick={createNewEdgeless}
        onAuxClick={createNewEdgeless}
        data-testid="new-edgeless-button-in-all-page"
      />
      {/* YOD-396: the sidebar's new-doc menu has offered Template since
          YOD-364 unified its two variants; this closes the gap the other way.
          A MenuSub row rather than a BlockCard because the template picker is
          itself a menu — same mounting the sidebar uses. */}
      {createFromTemplate ? (
        <MenuSub
          triggerOptions={{
            prefixIcon: <TemplateIcon width={20} height={20} />,
            'data-testid': 'template-button-in-all-page',
          }}
          subContentOptions={{
            sideOffset: 16,
            className: styles.templateMenu,
          }}
          items={
            <TemplateListMenuContentScrollable onSelect={createFromTemplate} />
          }
        >
          {t['Template']()}
        </MenuSub>
      ) : null}
      {importFile ? (
        <BlockCard
          title={t['com.affine.new_import']()}
          desc={t['com.affine.import_file']()}
          right={<ImportIcon width={20} height={20} />}
          onClick={importFile}
          data-testid="import-button-in-all-page"
        />
      ) : null}
      {importFromGitHub ? (
        <BlockCard
          title="Import from GitHub"
          desc="Import markdown files from a GitHub repository"
          right={<GithubIcon width={20} height={20} />}
          onClick={importFromGitHub}
          data-testid="import-github-button-in-all-page"
        />
      ) : null}
    </div>
  );
};

export const NewPageButton = ({
  createNewDoc,
  createNewPage,
  createNewEdgeless,
  createFromTemplate,
  importFile,
  importFromGitHub,
  size,
  children,
}: PropsWithChildren<NewPageButtonProps>) => {
  const [open, setOpen] = useState(false);

  const handleCreateFromTemplate = useCallback(
    (templateId: string) => {
      createFromTemplate?.(templateId);
      setOpen(false);
    },
    [createFromTemplate]
  );

  const handleCreateNewDoc: NewPageButtonProps['createNewDoc'] = useCallback(
    e => {
      createNewDoc(e);
      setOpen(false);
      track.allDocs.header.actions.createDoc();
    },
    [createNewDoc]
  );

  const handleCreateNewPage: NewPageButtonProps['createNewPage'] = useCallback(
    e => {
      createNewPage(e);
      setOpen(false);
      track.allDocs.header.actions.createDoc({ mode: 'page' });
    },
    [createNewPage]
  );

  const handleCreateNewEdgeless: NewPageButtonProps['createNewEdgeless'] =
    useCallback(
      e => {
        createNewEdgeless(e);
        setOpen(false);
        track.allDocs.header.actions.createDoc({
          mode: 'edgeless',
        });
      },
      [createNewEdgeless]
    );

  const handleImportFile = useCallback(() => {
    importFile?.();
    setOpen(false);
  }, [importFile]);

  const handleImportFromGitHub = useCallback(() => {
    importFromGitHub?.();
    setOpen(false);
  }, [importFromGitHub]);

  return (
    <Menu
      items={
        <CreateNewPagePopup
          createNewDoc={handleCreateNewDoc}
          createNewPage={handleCreateNewPage}
          createNewEdgeless={handleCreateNewEdgeless}
          createFromTemplate={
            createFromTemplate ? handleCreateFromTemplate : undefined
          }
          importFile={importFile ? handleImportFile : undefined}
          importFromGitHub={
            importFromGitHub ? handleImportFromGitHub : undefined
          }
        />
      }
      rootOptions={{
        open,
      }}
      contentOptions={{
        className: styles.menuContent,
        align: 'end',
        hideWhenDetached: true,
        onInteractOutside: useCallback(() => {
          setOpen(false);
        }, []),
      }}
    >
      <DropdownButton
        size={size}
        onClick={handleCreateNewDoc}
        onAuxClick={handleCreateNewPage}
        onClickDropDown={useCallback(() => setOpen(open => !open), [])}
        className={styles.button}
      >
        {children}
      </DropdownButton>
    </Menu>
  );
};
